// src/Services/authService.js

import {
  getAuth,
  signInAnonymously,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  GoogleAuthProvider,
  EmailAuthProvider,
  linkWithPopup,
  linkWithCredential,
  updatePassword as firebaseUpdatePassword,
  signOut as firebaseSignOut,
  deleteUser,
  getAdditionalUserInfo
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc, arrayUnion, deleteDoc, collection, query, where, writeBatch, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import { claimWelcomeBonus } from "./welcomeBonusService";
import analytics from "./analyticsService";
import { recordActivity, newAccountActivity } from "./activityService";
import { isTestSession } from "../utils/testSession";
import { authErrorMessage } from "./authErrors";
import { firstNameOf } from "../utils/validateDisplayName";

class AuthService {
  constructor() {
    this.auth = getAuth();
    this.currentUser = null;
    this.isInitialized = false;
    this.initPromise = this.initialize();
  }

  // Initialize auth and set up listener
  async initialize() {
    return new Promise((resolve) => {
      let firstCallback = true;
      // Stays subscribed for the life of the app. It used to unsubscribe after
      // the first callback, which left this.currentUser frozen at the startup
      // user: after a sign-in, sign-up or upgrade every getCurrentUserId() /
      // isAuthenticated() still answered with the old anonymous user until the
      // page was reloaded.
      onAuthStateChanged(this.auth, (user) => {
        this.currentUser = user;
        this.isInitialized = true;
        if (firstCallback) {
          firstCallback = false;
          // Identify with the Firebase UID immediately, anonymous or not, so
          // this session's events attach to a stable distinct_id from the
          // start. Linking (anon -> Google/email) preserves the UID, so no
          // re-identify is needed there; only a genuinely different UID
          // (fresh sign-in, not a link) needs identify() called again, which
          // happens at each of those call sites below.
          if (user) analytics.identify(user.uid, { is_anonymous: user.isAnonymous });
          resolve(user);
        }
        if (user) recordActivity(user.uid);
      });
    });
  }


  // Merge data from one user document into another (coins, histories, rooms, stats)
  async mergeUserData(fromUid, toUid) {
    if (!fromUid || !toUid || fromUid === toUid) return;

    try {
      const fromRef = doc(db, "users", fromUid);
      const toRef = doc(db, "users", toUid);
      const [fromSnap, toSnap] = await Promise.all([getDoc(fromRef), getDoc(toRef)]);

      if (!fromSnap.exists()) {
        // Nothing to merge
        return;
      }

      // Ensure destination exists
      if (!toSnap.exists()) {
        await this.createUserDocument(toUid);
      }

      const fromData = fromSnap.data() || {};
      const toData = (await getDoc(toRef)).data() || {};

      // Prefer summing, but if merge fails, carry the larger balance
      const fromCoins = fromData.coinBalance || 0;
      const toCoins = toData.coinBalance || 0;
      const mergedCoinBalance = fromCoins + toCoins;
      const mergedTotalCoinsSpent = (toData.totalCoinsSpent || 0) + (fromData.totalCoinsSpent || 0);

      const mergeArray = (a = [], b = []) => {
        const seen = new Set();
        const res = [];
        [...a, ...b].forEach((item) => {
          const key = JSON.stringify(item);
          if (!seen.has(key)) {
            seen.add(key);
            res.push(item);
          }
        });
        return res;
      };

      const mergedPurchaseHistory = mergeArray(toData.coinPurchaseHistory, fromData.coinPurchaseHistory);
      const mergedSpendingHistory = mergeArray(toData.coinSpendingHistory, fromData.coinSpendingHistory);
      const mergedRecentRooms = mergeArray(toData.recentRooms, fromData.recentRooms);

      const mergedStats = {
        ...(toData.stats || {}),
        totalGamesPlayed: (toData.stats?.totalGamesPlayed || 0) + (fromData.stats?.totalGamesPlayed || 0),
        totalWins: (toData.stats?.totalWins || 0) + (fromData.stats?.totalWins || 0),
        currentWinStreak: Math.max(toData.stats?.currentWinStreak || 0, fromData.stats?.currentWinStreak || 0),
        longestWinStreak: Math.max(toData.stats?.longestWinStreak || 0, fromData.stats?.longestWinStreak || 0),
        lastLoss: (toData.stats?.lastLoss ?? false) || (fromData.stats?.lastLoss ?? false)
      };

      try {
        await updateDoc(toRef, {
          coinBalance: mergedCoinBalance,
          totalCoinsSpent: mergedTotalCoinsSpent,
          coinPurchaseHistory: mergedPurchaseHistory || [],
          coinSpendingHistory: mergedSpendingHistory || [],
          recentRooms: mergedRecentRooms || [],
          stats: mergedStats,
          mergedFromAnonymousAt: new Date(),
          mergedFromUid: fromUid
        });

        // Clean up: remove source Firestore document
        await deleteDoc(fromRef);

        console.log(`✅ Merged data from ${fromUid} -> ${toUid}. Coins: ${mergedCoinBalance}`);
      } catch (updateError) {
        console.warn("⚠️ Merge update failed, falling back to larger balance:", updateError);
        
        // Fallback: use the larger balance if merge fails
        const fallbackBalance = Math.max(fromCoins, toCoins);
        await updateDoc(toRef, {
          coinBalance: fallbackBalance,
          totalCoinsSpent: mergedTotalCoinsSpent,
          coinPurchaseHistory: mergedPurchaseHistory || [],
          coinSpendingHistory: mergedSpendingHistory || [],
          recentRooms: mergedRecentRooms || [],
          stats: mergedStats,
          mergedFromAnonymousAt: new Date(),
          mergedFromUid: fromUid,
          mergeFallbackUsed: true
        });

        // Still clean up source document
        await deleteDoc(fromRef);
        console.log(`✅ Fallback merge completed. Coins: ${fallbackBalance} (larger of ${fromCoins}, ${toCoins})`);
      }
    } catch (error) {
      console.error("❌ Failed to merge user data:", error);
    }
  }

  // Makes sure there is a user, creating a guest (anonymous) account if needed.
  // Only for actions that must save something: browsing never creates an
  // account (see getExistingUserId). Concurrent callers share one sign-in, so
  // two parts of a screen asking at once can't create two guests.
  async ensureAuthenticated() {
    await this.initPromise;

    if (!this.currentUser) {
      this.guestSignIn ||= (async () => {
        try {
          const userCredential = await signInAnonymously(this.auth);
          this.currentUser = userCredential.user;
          analytics.identify(this.currentUser.uid, { is_anonymous: true });
          await this.createUserDocument(this.currentUser.uid);
          return this.currentUser;
        } catch (error) {
          console.error("❌ Guest sign-in failed:", error);
          throw error;
        } finally {
          this.guestSignIn = null;
        }
      })();
      await this.guestSignIn;
    }

    return this.currentUser;
  }


  // The user's id, creating a guest account if there is none. Use for actions
  // (create/join a room, play, buy, vote); for reads use getExistingUserId().
  async getCurrentUserId() {
    await this.ensureAuthenticated();
    return this.currentUser?.uid;
  }

  // The signed-in (or guest) user's id, or null for a visitor who hasn't done
  // anything yet. Never creates an account.
  async getExistingUserId() {
    await this.initPromise;
    return this.currentUser?.uid || null;
  }

  // Check if user is already authenticated (for quick checks)
  isAuthenticated() {
    return !!this.currentUser;
  }

  // Create or update user document in Firestore
  /** The displayName update for a sign-in: the player's chosen name is kept,
   *  and an account without one starts with the first name only. Signing in
   *  used to copy the full Google name over the chosen one every time. */
  async startingNameFields(userRef, fullName) {
    try {
      const snap = await getDoc(userRef);
      if (snap.exists() && snap.data().displayName) return {};
    } catch (error) {
      console.warn("Couldn't read the saved name; leaving it as is:", error);
      return {};
    }
    const name = firstNameOf(fullName);
    return name ? { displayName: name } : {};
  }

  async createUserDocument(uid, userData = {}) {
    try {
      const userRef = doc(db, "users", uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        // New user - create with all fields
        await setDoc(userRef, {
          uid,
          createdAt: new Date(),
          recentRooms: [],
          coinBalance: 0,
          totalCoinsSpent: 0,
          coinPurchaseHistory: [],
          coinSpendingHistory: [],
          // Add stats fields for new users
          stats: {
            totalGamesPlayed: 0,
            totalWins: 0,
            currentWinStreak: 0,
            longestWinStreak: 0,
            lastLoss: false
          },
          monthlyStats: {},
          weeklyStats: {},
          // Starts the account's activity record on the day it joined, so
          // the admin dashboard can count who comes back.
          ...newAccountActivity(),
          ...(isTestSession() ? { testAccount: true } : {}),
          ...userData
        });
        console.log("✅ User document created for:", uid);
      } else {
        // Existing user - add missing fields
        const existingData = userDoc.data();
        const updateData = {};
        if (isTestSession() && !existingData.testAccount) updateData.testAccount = true;

        // Check and add missing coin fields
        if (existingData.coinBalance === undefined) {
          updateData.coinBalance = 0;
          updateData.totalCoinsSpent = 0;
          updateData.coinPurchaseHistory = [];
        }

        if (!existingData.coinSpendingHistory) {
          updateData.coinSpendingHistory = [];
        }

        // Add missing stats field for existing users
        if (!existingData.stats) {
          updateData.stats = {
            totalGamesPlayed: 0,
            totalWins: 0,
            currentWinStreak: 0,
            longestWinStreak: 0,
            lastLoss: false
          };
          console.log("📊 Adding stats field to existing web user:", uid);
        }

        if (!existingData.monthlyStats) {
          updateData.monthlyStats = {};
        }

        if (!existingData.weeklyStats) {
          updateData.weeklyStats = {};
        }

        // Update document if we have changes
        if (Object.keys(updateData).length > 0) {
          await updateDoc(userRef, updateData);
          console.log("✅ Added missing fields to existing web user:", uid, Object.keys(updateData));
        }
      }
    } catch (error) {
      console.error("❌ Error creating user document:", error);
    }
  }

  // Add room to user's recent rooms
  async addRoomToUser(roomId, roomData) {
    try {
      const uid = await this.getCurrentUserId();
      const userRef = doc(db, "users", uid);

      const roomEntry = {
        roomId,
        roomName: roomData.roomName,
        isHost: roomData.isHost,
        joinedAt: new Date(), // This will be stored as Firebase Timestamp
        status: 'active'
      };

      await updateDoc(userRef, {
        activeRoomId: roomId,
        recentRooms: arrayUnion(roomEntry)
      });

      console.log("✅ Room added to user:", roomId);
    } catch (error) {
      console.error("❌ Error adding room to user:", error);
    }
  }

  // Get user's recent rooms
  async getUserRooms() {
    try {
      const uid = await this.getExistingUserId();
      if (!uid) return [];
      const userRef = doc(db, "users", uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        return userDoc.data().recentRooms || [];
      }
      return [];
    } catch (error) {
      console.error("❌ Error getting user rooms:", error);
      return [];
    }
  }


  async addCoinSpending(amount, purpose, roomId = null) {
    try {
      const uid = await this.getCurrentUserId();
      const userRef = doc(db, "users", uid);

      const spendingRecord = {
        amount,
        purpose,
        roomId,
        timestamp: new Date()
      };

      await updateDoc(userRef, {
        coinSpendingHistory: arrayUnion(spendingRecord)
      });

      console.log("✅ Coin spending recorded:", spendingRecord);
    } catch (error) {
      console.error("❌ Error recording coin spending:", error);
    }
  }

  async ensureSpendingHistoryField() {
    try {
      const uid = await this.getCurrentUserId();
      const userRef = doc(db, "users", uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        if (!data.coinSpendingHistory) {
          await updateDoc(userRef, {
            coinSpendingHistory: []
          });
          console.log("✅ Added coinSpendingHistory field to user document");
        }
      }
    } catch (error) {
      console.error("❌ Error ensuring spending history field:", error);
    }
  } async signInWithGoogle() {
    try {
      const provider = new GoogleAuthProvider();

      // If currently anonymous, prefer linking to preserve UID and data
      const anonUid = this.currentUser?.isAnonymous ? this.currentUser.uid : null;
      if (this.currentUser?.isAnonymous) {
        try {
          const linkResult = await linkWithPopup(this.currentUser, provider);
          const linkedUser = linkResult.user;
          console.log("✅ Linked anonymous account to Google:", linkedUser.uid);
          analytics.identify(linkedUser.uid, { is_anonymous: false, sign_in_method: "google" });

          // Ensure user doc has metadata
          const userRef = doc(db, "users", linkedUser.uid);
          await updateDoc(userRef, {
            lastLogin: new Date(),
            ...(await this.startingNameFields(userRef, linkedUser.displayName)),
            email: linkedUser.email,
            isAnonymous: false
          });

          claimWelcomeBonus("link");
          return { success: true, user: linkedUser, message: "Successfully linked with Google" };
        } catch (linkError) {
          // If credential already in use, fall back to sign-in then merge
          if (linkError?.code === 'auth/credential-already-in-use' || linkError?.code === 'auth/account-exists-with-different-credential') {
            console.warn("ℹ️ Link failed due to existing account, proceeding to sign in and merge.");
          } else {
            throw linkError;
          }
        }
      }

      const result = await signInWithPopup(this.auth, provider);

      console.log("✅ Google sign in successful:", result.user.uid);
      analytics.identify(result.user.uid, { is_anonymous: false, sign_in_method: "google" });

      // A brand-new Google user (e.g. a visitor creating an account) has no
      // player record yet, so make sure one exists before updating it.
      await this.createUserDocument(result.user.uid);

      // Update last login
      const userRef = doc(db, "users", result.user.uid);
      await updateDoc(userRef, {
        lastLogin: new Date(),
        ...(await this.startingNameFields(userRef, result.user.displayName)),
        email: result.user.email
      });

      // If we had an anonymous session before sign-in, merge its data into this account
      if (anonUid && anonUid !== result.user.uid) {
        await this.mergeUserData(anonUid, result.user.uid);
      }

      if (getAdditionalUserInfo(result)?.isNewUser) claimWelcomeBonus("create");

      return {
        success: true,
        user: result.user,
        message: "Successfully signed in with Google"
      };
    } catch (error) {
      console.error("❌ Google sign in error:", error);
      
      // Handle specific error cases for better UX
      if (error.code === 'auth/email-already-in-use') {
        // Extract email from error if available
        const email = error.customData?.email || null;
        return {
          success: false,
          error: 'email_exists',
          message: "This email is already registered with email/password. Please sign in with email/password instead, or contact support to link your accounts.",
          canLink: true,
          googleEmail: email
        };
      } else if (error.code === 'auth/popup-closed-by-user') {
        return {
          success: false,
          error: 'popup_closed',
          message: "Sign-in popup was closed. Please try again."
        };
      } else if (error.code === 'auth/cancelled-popup-request') {
        return {
          success: false,
          error: 'popup_cancelled',
          message: "Sign-in was interrupted. Please try again."
        };
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        return {
          success: false,
          error: 'account_exists',
          message: "This email is already registered with a different sign-in method. Please use the original sign-in method or contact support."
        };
      }
      
      return {
        success: false,
        message: this.getAuthErrorMessage(error)
      };
    }
  }


  // Sign in with email/password (for existing accounts). If anonymous, try link, else merge on sign-in.
  async signInWithEmail(email, password) {
    try {
      const anonUid = this.currentUser?.isAnonymous ? this.currentUser.uid : null;

      // If anonymous, try to link first (this covers the "register from anon" case when email is new)
      if (this.currentUser?.isAnonymous) {
        try {
          const credential = EmailAuthProvider.credential(email, password);
          const linkResult = await linkWithCredential(this.currentUser, credential);
          const linkedUser = linkResult.user;

          const linkedRef = doc(db, "users", linkedUser.uid);
          await updateDoc(linkedRef, { lastLogin: new Date(), email: linkedUser.email, isAnonymous: false });
          analytics.identify(linkedUser.uid, { is_anonymous: false, sign_in_method: "email" });

          return { success: true, user: linkedUser, message: "Successfully linked email to account" };
        } catch (linkError) {
          if (linkError?.code === 'auth/email-already-in-use') {
            console.warn("ℹ️ Email already in use, proceeding to sign in and merge.");
          } else {
            throw linkError;
          }
        }
      }

      const result = await signInWithEmailAndPassword(this.auth, email, password);

      console.log("✅ Email sign in successful:", result.user.uid);
      analytics.identify(result.user.uid, { is_anonymous: false, sign_in_method: "email" });

      // Update last login
      const userRef = doc(db, "users", result.user.uid);
      await updateDoc(userRef, {
        lastLogin: new Date()
      });

      // If we had an anonymous session before sign-in, merge its data into this account
      if (anonUid && anonUid !== result.user.uid) {
        await this.mergeUserData(anonUid, result.user.uid);
      }

      return {
        success: true,
        user: result.user,
        message: "Successfully signed in"
      };
    } catch (error) {
      console.error("❌ Email sign in error:", error);
      return {
        success: false,
        message: this.getAuthErrorMessage(error)
      };
    }
  }

  isUserSignedIn() {
    return !!this.currentUser;
  }

  // Allow adding email/password to an existing account (e.g., Google users)
  async linkEmailPasswordToCurrentUser(email, password) {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error("Not signed in");
      if (user.isAnonymous) throw new Error("Anonymous user. Use upgrade flow instead.");

      const credential = EmailAuthProvider.credential(email, password);
      const result = await linkWithCredential(user, credential);

      const userRef = doc(db, "users", result.user.uid);
      await updateDoc(userRef, { email: email, updatedAt: new Date() });

      return { success: true, user: result.user, message: "Email/password linked successfully" };
    } catch (error) {
      return { success: false, message: this.getAuthErrorMessage(error) };
    }
  }

  // Handle cross-provider account linking when Google sign-in encounters existing email/password account
  async linkGoogleToExistingEmailAccount(googleUser, email, password) {
    try {
      console.log("🔗 Attempting to link Google account to existing email/password account");
      
      // First, sign in with the existing email/password account
      const emailSignInResult = await signInWithEmailAndPassword(this.auth, email, password);
      
      if (!emailSignInResult.user) {
        throw new Error("Failed to sign in with email/password");
      }

      // Now try to link Google credential to this account
      // We'll use a different approach since we don't have the Google user object
      const provider = new GoogleAuthProvider();
      
      try {
        const linkResult = await linkWithPopup(emailSignInResult.user, provider);
        console.log("✅ Successfully linked Google to existing email account:", linkResult.user.uid);
        analytics.identify(linkResult.user.uid, { is_anonymous: false, sign_in_method: "google" });

        // Update user document with Google info
        const userRef = doc(db, "users", linkResult.user.uid);
        await updateDoc(userRef, {
          lastLogin: new Date(),
          ...(await this.startingNameFields(userRef, linkResult.user.displayName)),
          email: linkResult.user.email,
          googleLinked: true,
          linkedAt: new Date()
        });

        return {
          success: true,
          user: linkResult.user,
          message: "Successfully linked Google account to your existing email account!"
        };
      } catch (linkError) {
        console.error("❌ Failed to link Google credential:", linkError);
        
        if (linkError.code === 'auth/credential-already-in-use') {
          return {
            success: false,
            error: 'already_linked',
            message: "This Google account is already linked to another account."
          };
        } else if (linkError.code === 'auth/popup-closed-by-user') {
          return {
            success: false,
            error: 'popup_closed',
            message: "Google sign-in popup was closed. Please try again."
          };
        }
        
        throw linkError;
      }
    } catch (error) {
      console.error("❌ Failed to link Google to existing email account:", error);
      
      if (error.code === 'auth/wrong-password') {
        return {
          success: false,
          error: 'wrong_password',
          message: "Incorrect password for the existing email account. Please try again."
        };
      } else if (error.code === 'auth/user-not-found') {
        return {
          success: false,
          error: 'user_not_found',
          message: "No account found with this email address."
        };
      } else if (error.code === 'auth/credential-already-in-use') {
        return {
          success: false,
          error: 'already_linked',
          message: "This Google account is already linked to another account."
        };
      }
      
      return {
        success: false,
        error: 'link_failed',
        message: "Failed to link accounts. Please contact support."
      };
    }
  }

  // Update password for current email/password users
  async updatePassword(newPassword) {
    try {
      const user = this.auth.currentUser;
      if (!user) throw new Error("Not signed in");
      await firebaseUpdatePassword(user, newPassword);
      return { success: true, message: "Password updated" };
    } catch (error) {
      if (error?.code === 'auth/requires-recent-login') {
        return { success: false, message: 'Please re-authenticate and try again.' };
      }
      return { success: false, message: this.getAuthErrorMessage(error) };
    }
  }

  // Wait for Firebase auth to finish initializing (without creating anonymous session)
  waitForInit() {
    return this.initPromise;
  }

  // Add method for getting user ID without auto-creating anonymous
  getCurrentUserIdIfExists() {
    return this.currentUser?.uid || null;
  }
  // Sign out
  async signOut() {
    try {
      await firebaseSignOut(this.auth);

      console.log("✅ User signed out successfully");

      // Don't inherit the signed-out user's event history. No new guest is
      // created here: they're a visitor until they next do something.
      analytics.reset();

      return {
        success: true,
        message: "Successfully signed out"
      };
    } catch (error) {
      console.error("❌ Sign out error:", error);
      return {
        success: false,
        message: "Failed to sign out"
      };
    }
  }

  // Plain-language message for any auth error (never raw Firebase text).
  getAuthErrorMessage(error) {
    return authErrorMessage(error, "We couldn't sign you in");
  }

  // Emails a password-reset link. Always reports success for an unknown email
  // (Firebase does the same), so the form can't be used to probe for accounts.
  async sendPasswordReset(email) {
    try {
      await sendPasswordResetEmail(this.auth, String(email || "").trim());
      return { success: true };
    } catch (error) {
      if (error?.code === "auth/user-not-found") return { success: true };
      return { success: false, message: authErrorMessage(error, "We couldn't send the reset email") };
    }
  }

  // Check if user has any rooms (for showing "View My Rooms" button)
  async hasRooms() {
    const rooms = await this.getUserRooms();
    return rooms.length > 0;
  }

  async ensureUserHasStats() {
    try {
      const uid = await this.getCurrentUserId();
      await this.createUserDocument(uid);
      console.log("✅ Web user stats migration completed");
    } catch (error) {
      console.error("❌ Error migrating web user stats:", error);
    }
  }

  async deleteUserRooms(userId) {
    try {
      const roomsRef = collection(db, "rooms");
      const userRoomsQuery = query(roomsRef, where("hostId", "==", userId));
      const roomSnapshots = await getDocs(userRoomsQuery);

      if (roomSnapshots.empty) {
        console.log("✅ No rooms to delete");
        return;
      }

      const batch = writeBatch(db);
      roomSnapshots.forEach((roomDoc) => {
        batch.delete(roomDoc.ref);
      });

      await batch.commit();
      console.log(`✅ Deleted ${roomSnapshots.size} rooms`);
    } catch (error) {
      console.error("❌ Error deleting user rooms:", error);
      throw error;
    }
  }

  async removeUserFromRooms(userId) {
    try {
      const roomsRef = collection(db, "rooms");
      const allRoomsSnapshot = await getDocs(roomsRef);

      const batch = writeBatch(db);
      let updateCount = 0;

      allRoomsSnapshot.forEach((roomDoc) => {
        const roomData = roomDoc.data();
        const participants = roomData.participants || {};

        if (participants[userId]) {
          delete participants[userId];
          batch.update(roomDoc.ref, { participants });
          updateCount++;
        }
      });

      if (updateCount > 0) {
        await batch.commit();
        console.log(`✅ Removed user from ${updateCount} rooms`);
      }
    } catch (error) {
      console.error("❌ Error removing user from rooms:", error);
      throw error;
    }
  }

  async deleteAccount() {
    try {
      const user = this.auth.currentUser;

      if (!user) {
        return {
          success: false,
          message: "No user is currently signed in"
        };
      }

      if (user.isAnonymous) {
        return {
          success: false,
          message: "Cannot delete anonymous accounts. Please sign in first."
        };
      }

      const userId = user.uid;
      console.log("🗑️ Starting account deletion for:", userId);

      await this.deleteUserRooms(userId);
      await this.removeUserFromRooms(userId);

      const userRef = doc(db, "users", userId);
      await deleteDoc(userRef);
      console.log("✅ User document deleted");

      await deleteUser(user);
      console.log("✅ Firebase Auth user deleted");

      this.currentUser = null;
      analytics.reset();

      return {
        success: true,
        message: "Account deleted successfully"
      };
    } catch (error) {
      console.error("❌ Error deleting account:", error);

      if (error.code === "auth/requires-recent-login") {
        return {
          success: false,
          message: "Please re-authenticate before deleting your account"
        };
      }

      return {
        success: false,
        message: authErrorMessage(error, "We couldn't delete your account")
      };
    }
  }
}



// Export singleton instance
export const authService = new AuthService();
export default authService;